import React, { type ReactNode } from 'react';
import { EmptyState, type EmptyStateProps } from './empty-state';
import { ErrorState } from './error-state';
import { LoadingSkeleton } from './loading-skeleton';
import { Card, CardContent } from './card';
import { cn } from '../lib/utils';
import { PageTitle } from './typography';
import { SectionTabsSlot } from './sub-page-tabs/sub-page-tabs';

export interface DashboardPageHeaderProps {
  title: string;
  description: string;
  actions?: ReactNode;
  /**
   * Optional slot rendered inline with the title (typically a small help /
   * info icon that opens a Popover explaining the screen). Used by the
   * `AboutThisScreen` pattern to move explanatory walls off the page.
   */
  infoSlot?: ReactNode;
  className?: string;
}

export function DashboardPageHeader({
  title,
  description,
  actions,
  infoSlot,
  className,
}: DashboardPageHeaderProps) {
  return (
    <div className={cn('flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3', className)}>
      <div className="min-w-0 flex-[1_1_16rem] max-w-4xl">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <PageTitle className="min-w-0 flex-[1_1_16rem] break-normal [overflow-wrap:break-word]">
            {title}
          </PageTitle>
          {infoSlot ? <div className="flex items-center">{infoSlot}</div> : null}
        </div>
        <p className="experience-candidate-body mt-1 break-words font-body text-body-sm text-text-muted">
          {description}
        </p>
      </div>
      {actions ? (
        <div className="ml-auto flex min-w-0 max-w-full flex-[0_1_auto] flex-wrap items-center justify-start gap-2 [&>div]:flex [&>div]:flex-wrap [&>div]:gap-2 [&_button]:min-w-fit [&_a]:min-w-fit [&_button]:whitespace-nowrap [&_button]:shrink-0 [&_button_*]:whitespace-nowrap [&_a]:whitespace-nowrap [&_a]:shrink-0 [&_a_*]:whitespace-nowrap">
          {actions}
        </div>
      ) : null}
      <SectionTabsSlot />
    </div>
  );
}

export interface DashboardLoadingStateProps extends DashboardPageHeaderProps {
  cards?: number;
  lines?: number;
}

export function DashboardLoadingState({
  title,
  description,
  actions,
  cards = 4,
  lines = 3,
  className,
}: DashboardLoadingStateProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <DashboardPageHeader title={title} description={description} actions={actions} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: cards }).map((_, index) => (
          <Card key={index}>
            <CardContent className="pt-5">
              <LoadingSkeleton lines={lines} />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export interface DashboardErrorStateProps extends DashboardPageHeaderProps {
  message: string;
  onRetry?: () => void;
  referenceCode?: string;
  retryLabel?: string;
}

export function DashboardErrorState({
  title,
  description,
  actions,
  message,
  onRetry,
  referenceCode,
  retryLabel,
  infoSlot,
  className,
}: DashboardErrorStateProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <DashboardPageHeader
        title={title}
        description={description}
        actions={actions}
        infoSlot={infoSlot}
      />
      <ErrorState
        message={message}
        onRetry={onRetry}
        referenceCode={referenceCode}
        retryLabel={retryLabel}
      />
    </div>
  );
}

export interface DashboardEmptyStateProps
  extends DashboardPageHeaderProps, Omit<EmptyStateProps, 'title' | 'description' | 'className'> {
  emptyTitle?: string;
  emptyDescription?: string;
  emptyClassName?: string;
  emptyActions?: ReactNode;
}

export function DashboardEmptyState({
  title,
  description,
  actions,
  className,
  emptyTitle,
  emptyDescription,
  emptyClassName,
  emptyActions,
  ...emptyStateProps
}: DashboardEmptyStateProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <DashboardPageHeader title={title} description={description} actions={actions} />
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        className={emptyClassName}
        actions={emptyActions}
        {...emptyStateProps}
      />
    </div>
  );
}
